import { useQuery } from '@tanstack/react-query'
import {
  getMyAgency,
  getAgencyDashboard,
  getAgencyCounselors,
  getAgencyStats
} from '@/services/agency'
import { useAuth } from '@/contexts/AuthContext'

// Query keys
export const agencyKeys = {
  all: ['agency'] as const,
  myAgency: () => [...agencyKeys.all, 'my-agency'] as const,
  dashboard: () => [...agencyKeys.all, 'dashboard'] as const,
  counselors: (agencyId: string, params?: any) => [...agencyKeys.all, 'counselors', agencyId, params] as const,
  stats: (agencyId: string, timeframe?: string) => [...agencyKeys.all, 'stats', agencyId, timeframe] as const
}

// Get current user's agency
export const useMyAgency = () => {
  const { isAgencyAdmin, isLoading } = useAuth()
  
  return useQuery({
    queryKey: agencyKeys.myAgency(),
    queryFn: getMyAgency,
    enabled: !isLoading && isAgencyAdmin, // Only fetch if auth is loaded and user is agency admin
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Get agency dashboard data
export const useAgencyDashboard = () => {
  const { isAgencyAdmin, isLoading } = useAuth()
  
  return useQuery({
    queryKey: agencyKeys.dashboard(),
    queryFn: getAgencyDashboard,
    enabled: !isLoading && isAgencyAdmin, // Only fetch if auth is loaded and user is agency admin
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

// Get agency counselors
export const useAgencyCounselors = (agencyId?: string, params?: any) => {
  const { isAgencyAdmin, isLoading } = useAuth()
  
  return useQuery({
    queryKey: agencyKeys.counselors(agencyId || '', params),
    queryFn: () => getAgencyCounselors(agencyId!, params),
    enabled: !isLoading && isAgencyAdmin && !!agencyId, // Only fetch if auth is loaded, user is agency admin and agencyId exists
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

// Get agency statistics
export const useAgencyStats = (agencyId?: string, timeframe?: 'week' | 'month' | 'quarter' | 'year') => {
  const { isAgencyAdmin, isLoading } = useAuth()
  
  return useQuery({
    queryKey: agencyKeys.stats(agencyId || '', timeframe),
    queryFn: () => getAgencyStats(agencyId!, timeframe),
    enabled: !isLoading && isAgencyAdmin && !!agencyId, // Only fetch if auth is loaded, user is agency admin and agencyId exists
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}